import React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { marketingPalette, marketingSymbols } from '../../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTabSx,
  publicTextFieldSx,
} from '../common/PublicPageFrame';

export const caseManagerPalette = marketingPalette;
export { publicBodyFontFamily, publicHeadingFontFamily, publicPrimaryButtonSx, publicSecondaryButtonSx, publicTabSx, publicTextFieldSx };

export const caseManagerPageSx = {
  minHeight: '100%',
  py: { xs: 3, md: 5 },
};

export const caseManagerHeroSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '28px', md: '34px' },
  p: { xs: 3, sm: 3.5, md: 4.5 },
  background:
    'linear-gradient(140deg, rgba(18, 61, 103, 0.98) 0%, rgba(21, 83, 129, 0.97) 54%, rgba(49, 182, 228, 0.93) 100%)',
  color: caseManagerPalette.white,
  boxShadow: '0 34px 84px rgba(18, 61, 103, 0.16)',
};

export const caseManagerSurfaceSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '26px', md: '30px' },
  border: `1px solid ${alpha(caseManagerPalette.ink, 0.08)}`,
  bgcolor: alpha('#ffffff', 0.94),
  boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
};

export const caseManagerInsetSurfaceSx = {
  borderRadius: '22px',
  border: `1px solid ${alpha(caseManagerPalette.ink, 0.08)}`,
  bgcolor: alpha(caseManagerPalette.heroBlueMist, 0.58),
};

export const caseManagerSubtlePillSx = {
  borderRadius: '999px',
  bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.10),
  color: caseManagerPalette.heroBlueDeep,
  fontWeight: 800,
  fontFamily: publicBodyFontFamily,
};

export const caseManagerMetaPillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.65,
  px: 1.15,
  py: 0.55,
  borderRadius: '999px',
  bgcolor: alpha(caseManagerPalette.heroBlue, 0.08),
  color: caseManagerPalette.inkSoft,
  fontSize: '0.84rem',
  fontWeight: 700,
  fontFamily: publicBodyFontFamily,
};

export const caseManagerSelectSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '18px',
    bgcolor: alpha('#ffffff', 0.96),
    fontFamily: publicBodyFontFamily,
    fontWeight: 700,
    '& fieldset': {
      borderColor: alpha(caseManagerPalette.ink, 0.10),
    },
    '&:hover fieldset': {
      borderColor: alpha(caseManagerPalette.heroBlueMid, 0.36),
    },
    '&.Mui-focused fieldset': {
      borderColor: caseManagerPalette.heroBlueMid,
      borderWidth: '1px',
    },
  },
  '& .MuiInputLabel-root': {
    color: caseManagerPalette.inkSoft,
    fontFamily: publicBodyFontFamily,
  },
};

export const caseManagerDialogPaperProps = {
  sx: {
    borderRadius: '28px',
    border: `1px solid ${alpha(caseManagerPalette.ink, 0.08)}`,
    boxShadow: '0 30px 80px rgba(18, 61, 103, 0.14)',
  },
};

export function CaseManagerHeroSymbol() {
  return (
    <Box
      component="img"
      src={marketingSymbols.silhouette}
      alt=""
      aria-hidden
      sx={{
        position: 'absolute',
        right: { xs: -16, md: 24 },
        top: { xs: -22, md: -48 },
        width: { xs: 160, md: 260 },
        opacity: 0.14,
        pointerEvents: 'none',
      }}
    />
  );
}

export function CaseEditorStepFrame({ eyebrow, title, description, meta, children }) {
  const hasHeader = Boolean(eyebrow || title || description || meta);

  return (
    <Box>
      {hasHeader ? (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ md: 'flex-start' }}
          sx={{ mb: 3 }}
        >
          <Box sx={{ maxWidth: 840 }}>
            {eyebrow ? (
              <Typography
                sx={{
                  mb: 0.8,
                  color: caseManagerPalette.heroBlueMid,
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontFamily: publicBodyFontFamily,
                }}
              >
                {eyebrow}
              </Typography>
            ) : null}
            <Typography
              sx={{
                color: caseManagerPalette.ink,
                fontSize: { xs: '1.65rem', md: '2rem' },
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
                fontFamily: publicHeadingFontFamily,
              }}
            >
              {title}
            </Typography>
            {description ? (
              <Typography
                sx={{
                  mt: 1,
                  color: caseManagerPalette.inkSoft,
                  lineHeight: 1.72,
                  fontFamily: publicBodyFontFamily,
                }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>
          {meta ? <Box sx={{ flexShrink: 0 }}>{meta}</Box> : null}
        </Stack>
      ) : null}
      {children}
    </Box>
  );
}

export function CaseStepStatusChip({ label, color = 'default' }) {
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        fontWeight: 800,
        fontFamily: publicBodyFontFamily,
      }}
    />
  );
}
